import type { ReactElement } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  Box,
  IconButton,
  Line,
  Menu,
  MenuItem,
  Modal,
  Text,
  color,
  config,
  toRem,
} from 'folds';
import { Avatar, AvatarShape } from '$components/avatar';
import type { VirtualItem } from '@tanstack/react-virtual';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { MatrixClient, Room, RoomJoinRulesEventContent } from '$types/matrix-sdk';
import { JoinRule, EventType, KnownMembership } from '$types/matrix-sdk';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { mDirectAtom } from '$state/mDirectList';
import { NavCategory, NavCategoryHeader, NavItem, NavItemContent, NavLink } from '$components/nav';
import {
  getSpaceLobbyPath,
  getSpaceRoomPath,
  getSpaceForumPath,
  getSpaceSearchPath,
} from '$pages/pathUtils';
import { getCanonicalAliasOrRoomId, isRoomAlias, mxcUrlToHttp } from '$utils/matrix';
import { useSelectedOrLastRoom } from '$hooks/router/useSelectedRoom';
import { useSpaceLobbySelected, useSpaceSearchSelected } from '$hooks/router/useSelectedSpace';
import { useSpace } from '$hooks/useSpace';
import { VirtualTile } from '$components/virtualizer';
import { spaceRoomsAtom } from '$state/spaceRooms';
import { RoomNavCategoryButton, RoomNavItem } from '$features/room-nav';
import { SpaceNavItem } from '$features/space-nav';
import { makeNavCategoryId } from '$state/closedNavCategories';
import { roomToUnreadAtom } from '$state/room/roomToUnread';
import { useCategoryHandler } from '$hooks/useCategoryHandler';
import { useNavToActivePathMapper } from '$hooks/useNavToActivePathMapper';
import { useRoomName } from '$hooks/useRoomMeta';
import type { HierarchyItem } from '$hooks/useSpaceHierarchy';
import { getSpaceHierarchyItemKey, useSpaceJoinedHierarchy } from '$hooks/useSpaceHierarchy';
import { allRoomsAtom } from '$state/room-list/roomList';
import { PageNavContent, PageNavHeader } from '$components/page';
import { PageNavShell } from '$components/page/PageNavShell';
import { usePowerLevels } from '$hooks/usePowerLevels';
import { useRecursiveChildScopeFactory, useSpaceChildren } from '$state/hooks/roomList';
import {
  Checks,
  chipIcon,
  composerIcon,
  DotsThreeOutlineVerticalIcon,
  Flag,
  GearSix,
  Link,
  Lock,
  MagnifyingGlass,
  menuIcon,
  ShareNetwork,
  SignOut,
  Terminal,
  UserPlus,
} from '$components/icons/phosphor';
import { roomToParentsAtom } from '$state/room/roomToParents';
import { roomToChildrenAtom } from '$state/room/roomToChildren';
import { markAsRead } from '$utils/notifications';
import { useRoomsUnread } from '$state/hooks/unread';
import { UseStateProvider } from '$components/UseStateProvider';
import { LeaveSpacePrompt } from '$components/leave-space-prompt';
import { copyToClipboard } from '$utils/dom';
import { shareText } from '$utils/share';
import { useClosedNavCategoriesAtom } from '$state/hooks/closedNavCategories';
import { useStateEvent } from '$hooks/useStateEvent';

import { getMatrixToRoom } from '$plugins/matrix-to';
import { getViaServers } from '$plugins/via-servers';
import { useSetting } from '$state/hooks/settings';
import { settingsAtom, ShowRoomIcon } from '$state/settings';
import {
  getRoomNotificationMode,
  useRoomsNotificationPreferencesContext,
} from '$hooks/useRoomsNotificationPreferences';
import { useRoomNavigate } from '$hooks/useRoomNavigate';
import { useRoomCreators } from '$hooks/useRoomCreators';
import { useRoomPermissions } from '$hooks/useRoomPermissions';
import { ContainerColor } from '$styles/ContainerColor.css';
import { AsyncStatus, useAsyncCallback } from '$hooks/useAsyncCallback';
import { AsyncError } from '$components/AsyncError';
import { InviteUserPrompt } from '$components/invite-user-prompt';
import { useCallEmbed } from '$hooks/useCallEmbed';
import { createDebugLogger } from '$utils/debugLogger';
import { SidebarResizer } from '$pages/client/sidebar/SidebarResizer';
import { useSidebarWidth } from '$hooks/useSidebarWidth';
import { RoomAvatar } from '$components/room-avatar';
import { getRoomAvatarUrl } from '$utils/room/display';
import { nameInitials } from '$utils/common';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { CustomStateEvent, CustomRoomType } from '$types/matrix/room';
import type { RoomBannerContent } from '$types/matrix-sdk-events';
import { ModalWide } from '$styles/Modal.css';
import { ImageViewer } from '$components/image-viewer';
import { reportMediaLoadFailure } from '$utils/mediaLoadDiagnostics';
import * as css from './styles.css';
import { Image as MediaImage } from '$components/media';
import { useRenderableMediaUrl } from '$hooks/useRenderableMediaUrl';
import { ModalOverlay } from '$components/modal-overlay/ModalOverlay';
import { useOpenRoomSettings } from '$state/hooks/roomSettings';
import { ResponsiveMenu } from '$components/ResponsiveMenu';
import { useMenuAnchor } from '$hooks/useMenuAnchor';
import { Button } from '$components/button';

const debugLog = createDebugLogger('Space');

type SpaceMenuProps = {
  room: Room;
  requestClose: () => void;
};

const SpaceMenu = forwardRef<HTMLDivElement, SpaceMenuProps>(({ room, requestClose }, ref) => {
  const mx = useMatrixClient();
  const [hideReads] = useSetting(settingsAtom, 'hideReads');
  const [developerTools] = useSetting(settingsAtom, 'developerTools');
  const roomToParents = useAtomValue(roomToParentsAtom);
  const powerLevels = usePowerLevels(room);
  const creators = useRoomCreators(room);

  const permissions = useRoomPermissions(creators, powerLevels);
  const canInvite = permissions.action('invite', mx.getSafeUserId());
  const openRoomSettings = useOpenRoomSettings();
  const { navigateRoom } = useRoomNavigate();

  const [invitePrompt, setInvitePrompt] = useState(false);

  const allChild = useSpaceChildren(
    allRoomsAtom,
    room.roomId,
    useRecursiveChildScopeFactory(mx, roomToParents)
  );
  const unread = useRoomsUnread(allChild, roomToUnreadAtom);

  const handleMarkAsRead = () => {
    allChild.forEach((childRoomId) => markAsRead(mx, childRoomId, hideReads, true));
    requestClose();
  };

  const handleCopyLink = () => {
    const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, room.roomId);
    const viaServers = isRoomAlias(roomIdOrAlias) ? undefined : getViaServers(room);
    copyToClipboard(getMatrixToRoom(roomIdOrAlias, viaServers));
    requestClose();
  };

  const handleShareLink = () => {
    const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, room.roomId);
    const viaServers = isRoomAlias(roomIdOrAlias) ? undefined : getViaServers(room);
    shareText(getMatrixToRoom(roomIdOrAlias, viaServers)).catch(() => {});
    requestClose();
  };

  const handleInvite = () => {
    setInvitePrompt(true);
  };

  const handleRoomSettings = () => {
    openRoomSettings(room.roomId);
    requestClose();
  };

  const handleOpenTimeline = () => {
    debugLog.info('ui', 'Space timeline opened', { roomId: room.roomId });
    navigateRoom(room.roomId);
    requestClose();
  };

  return (
    <Menu ref={ref} style={{ maxWidth: toRem(160), width: '100vw' }}>
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        {invitePrompt && room && (
          <InviteUserPrompt
            room={room}
            requestClose={() => {
              setInvitePrompt(false);
              requestClose();
            }}
          />
        )}
        <MenuItem
          onClick={handleMarkAsRead}
          size="300"
          after={menuIcon(Checks)}
          radii="300"
          disabled={!unread}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Mark as Read
          </Text>
        </MenuItem>
      </Box>
      <Line variant="Surface" size="300" />
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <MenuItem
          onClick={handleInvite}
          variant="Primary"
          fill="None"
          size="300"
          after={menuIcon(UserPlus)}
          radii="300"
          aria-pressed={invitePrompt}
          disabled={!canInvite}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Invite
          </Text>
        </MenuItem>
        <MenuItem onClick={handleCopyLink} size="300" after={menuIcon(Link)} radii="300">
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Copy Link
          </Text>
        </MenuItem>
        <MenuItem onClick={handleShareLink} size="300" after={menuIcon(ShareNetwork)} radii="300">
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Share Link
          </Text>
        </MenuItem>
        <MenuItem onClick={handleRoomSettings} size="300" after={menuIcon(GearSix)} radii="300">
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Space Settings
          </Text>
        </MenuItem>
        {developerTools && (
          <MenuItem onClick={handleOpenTimeline} size="300" after={menuIcon(Terminal)} radii="300">
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Event Timeline
            </Text>
          </MenuItem>
        )}
      </Box>
      <Line variant="Surface" size="300" />
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <UseStateProvider initial={false}>
          {(promptLeave, setPromptLeave) => (
            <>
              <MenuItem
                onClick={() => setPromptLeave(true)}
                variant="Critical"
                fill="None"
                size="300"
                after={menuIcon(SignOut)}
                radii="300"
                aria-pressed={promptLeave}
              >
                <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
                  Leave Space
                </Text>
              </MenuItem>
              {promptLeave && (
                <LeaveSpacePrompt
                  roomId={room.roomId}
                  onDone={requestClose}
                  onCancel={() => setPromptLeave(false)}
                />
              )}
            </>
          )}
        </UseStateProvider>
      </Box>
    </Menu>
  );
});

function SpaceHeader({ hideText, mx }: { hideText?: boolean; mx: MatrixClient }) {
  const space = useSpace();
  const spaceName = useRoomName(space);
  const menu = useMenuAnchor();
  const useAuthentication = useMediaAuthentication();

  const joinRules = useStateEvent(
    space,
    EventType.RoomJoinRules
  )?.getContent<RoomJoinRulesEventContent>();

  const [showBanners] = useSetting(settingsAtom, 'showRoomBanners');
  const [roomBannerHeight, setRoomBannerHeight] = useSetting(settingsAtom, 'roomBannerHeight');
  const [curHeight, setCurHeight] = useState(roomBannerHeight);
  useEffect(() => {
    setCurHeight(roomBannerHeight);
  }, [roomBannerHeight]);

  const bannerState = useStateEvent(space, CustomStateEvent.RoomBanner);
  const bannerMXC = bannerState?.getContent<RoomBannerContent>()?.url;
  const rawBannerURI = mxcUrlToHttp(mx, bannerMXC ?? '', useAuthentication);
  const bannerURI = useRenderableMediaUrl(rawBannerURI || undefined);
  const hasBanner = !!(bannerURI && !hideText && showBanners);

  const [bannerViewerOpen, setBannerViewerOpen] = useState(false);
  useEffect(() => {
    if (!hasBanner) setBannerViewerOpen(false);
  }, [hasBanner]);

  return (
    <>
      <div className={hasBanner ? css.RoomCoverHeaderContainer : ''}>
        <div
          className={
            hasBanner ? css.RoomCoverNavContainer : css.RoomCoverlessNavContainer({ hideText })
          }
        >
          <PageNavHeader outlined={!hasBanner} size="600">
            {hideText ? (
              <Box alignItems="Center" grow="Yes" justifyContent="Center">
                <Avatar
                  size={hideText ? undefined : '200'}
                  shape={AvatarShape.Squircle}
                  onClick={menu.triggerProps.onClick}
                >
                  <RoomAvatar
                    roomId={space.roomId}
                    src={getRoomAvatarUrl(mx, space, 96, useAuthentication)}
                    uniformIcons
                    alt={spaceName}
                    renderFallback={() => (
                      <Text as="span" size="H6">
                        {nameInitials(spaceName)}
                      </Text>
                    )}
                  />
                </Avatar>
              </Box>
            ) : (
              <Box grow="Yes" gap="300">
                <Box
                  grow="Yes"
                  alignItems="Center"
                  gap="100"
                  style={hasBanner ? { color: '#fff' } : {}}
                >
                  <Text size="H4" truncate>
                    {spaceName}
                  </Text>
                  {joinRules?.join_rule !== JoinRule.Public && chipIcon(Lock)}
                </Box>
                <Box shrink="No">
                  <IconButton
                    aria-pressed={!!menu.anchor}
                    variant="Background"
                    style={hasBanner ? { backgroundColor: 'transparent', color: '#fff' } : {}}
                    onClick={menu.triggerProps.onClick}
                  >
                    {composerIcon(DotsThreeOutlineVerticalIcon, {
                      weight: menu.anchor ? 'fill' : 'regular',
                    })}
                  </IconButton>
                </Box>
              </Box>
            )}
          </PageNavHeader>
          <ResponsiveMenu
            anchor={menu.anchor}
            requestClose={menu.close}
            position="Bottom"
            align="End"
            offset={6}
            menu={<SpaceMenu room={space} requestClose={menu.close} />}
          />
        </div>
      </div>
      {hasBanner && (
        <>
          <Box shrink="No" className={css.RoomCoverContainer} style={{ height: toRem(curHeight) }}>
            <div className={css.RoomCover}>
              <button
                type="button"
                className={css.RoomCoverImageButton}
                aria-label={`View ${spaceName} banner`}
                onClick={() => setBannerViewerOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setBannerViewerOpen(true);
                  }
                }}
              >
                <MediaImage
                  className={css.RoomCoverImage}
                  src={bannerURI}
                  alt=""
                  draggable="false"
                  onError={() => reportMediaLoadFailure('room_banner')}
                />
              </button>
              <SidebarResizer
                setCurWidth={setCurHeight}
                sidebarWidth={roomBannerHeight}
                setSidebarWidth={setRoomBannerHeight}
                instep={56}
                outstep={66}
                minValue={56}
                maxValue={500}
                topSided
              />
            </div>
          </Box>
        </>
      )}
      {hasBanner && bannerViewerOpen && (
        <ModalOverlay requestClose={() => setBannerViewerOpen(false)}>
          <Modal
            className={ModalWide}
            size="500"
            onContextMenu={(evt: React.MouseEvent) => evt.stopPropagation()}
          >
            <ImageViewer
              src={bannerURI}
              alt={`${spaceName} banner`}
              requestClose={() => setBannerViewerOpen(false)}
            />
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
}

type SpaceTombstoneProps = { roomId: string; replacementRoomId: string };
function SpaceTombstone({ roomId, replacementRoomId }: SpaceTombstoneProps) {
  const mx = useMatrixClient();
  const { navigateSpace } = useRoomNavigate();

  const [joinState, handleJoin] = useAsyncCallback(
    useCallback(() => {
      const currentRoom = mx.getRoom(roomId);
      const via = currentRoom ? getViaServers(currentRoom) : [];
      return mx.joinRoom(replacementRoomId, {
        viaServers: via,
      });
    }, [mx, roomId, replacementRoomId])
  );
  const replacementRoom = mx.getRoom(replacementRoomId);

  const handleOpen = () => {
    if (replacementRoom) navigateSpace(replacementRoom.roomId);
    if (joinState.status === AsyncStatus.Success) navigateSpace(joinState.data.roomId);
  };

  return (
    <Box
      style={{
        padding: config.space.S200,
        borderRadius: config.radii.R400,
        borderWidth: config.borderWidth.B300,
      }}
      className={ContainerColor({ variant: 'Surface' })}
      direction="Column"
      gap="300"
    >
      <Box direction="Column" grow="Yes" gap="100">
        <Text size="L400">Space Upgraded</Text>
        <Text size="T200">This space has been replaced and is no longer active.</Text>
        <AsyncError state={joinState} />
      </Box>
      <Box direction="Column" shrink="No">
        {replacementRoom?.getMyMembership() === KnownMembership.Join ||
        joinState.status === AsyncStatus.Success ? (
          <Button onClick={handleOpen} size="300" variant="Success" fill="Solid" radii="300">
            <Text size="B300">Open New Space</Text>
          </Button>
        ) : (
          <Button
            loading={joinState.status === AsyncStatus.Loading}
            spinnerSize="100"
            spinnerVariant="Primary"
            spinnerFill="Solid"
            size="300"
            variant="Primary"
            fill="Solid"
            radii="300"
            onClick={handleJoin}
          >
            <Text size="B300">Join New Space</Text>
          </Button>
        )}
      </Box>
    </Box>
  );
}

export function Space() {
  const mx = useMatrixClient();
  const space = useSpace();
  useNavToActivePathMapper(space.roomId);
  const spaceIdOrAlias = getCanonicalAliasOrRoomId(mx, space.roomId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mDirects = useAtomValue(mDirectAtom);
  const roomToUnread = useAtomValue(roomToUnreadAtom);
  const roomToParents = useAtomValue(roomToParentsAtom);
  const roomToChildren = useAtomValue(roomToChildrenAtom);
  const allRooms = useAtomValue(allRoomsAtom);
  const [spaceRooms] = useAtom(spaceRoomsAtom);
  const allJoinedRooms = useMemo(() => new Set(allRooms), [allRooms]);
  const notificationPreferences = useRoomsNotificationPreferencesContext();

  const {
    curWidth,
    setCurWidth,
    roomSidebarWidth,
    setRoomSidebarWidth,
    setIsResizingSidebar,
    isMobile,
    hideText,
    oldSidebar,
  } = useSidebarWidth();

  const [showRoomIconGeneral] = useSetting(settingsAtom, 'showRoomIcon');
  const [showRoomIconArray] = useSetting(settingsAtom, 'perRoomShowRoomIcon');
  const showRoomIcon =
    showRoomIconArray.find((item) => item.roomId === space.roomId)?.display ?? showRoomIconGeneral;
  const showIcons = () => {
    if (showRoomIcon === ShowRoomIcon.Always) return true;
    if (showRoomIcon === ShowRoomIcon.Never) return false;
    return curWidth < 144;
  };
  const [joinCallOnSingleClick] = useSetting(settingsAtom, 'joinCallOnSingleClick');

  const tombstoneEvent = useStateEvent(space, EventType.RoomTombstone);
  const selectedRoomId = useSelectedOrLastRoom();
  const lobbySelected = useSpaceLobbySelected(spaceIdOrAlias);
  const searchSelected = useSpaceSearchSelected(spaceIdOrAlias);
  const callEmbed = useCallEmbed();

  const [closedCategories, setClosedCategories] = useAtom(useClosedNavCategoriesAtom());

  const getRoom = useCallback(
    (rId: string): Room | undefined => {
      if (allJoinedRooms.has(rId)) {
        return mx.getRoom(rId) ?? undefined;
      }
      return undefined;
    },
    [mx, allJoinedRooms]
  );

  const closedCategoriesCache = useRef(new Map());
  closedCategoriesCache.current.clear();

  /**
   * Recursively checks if a given parentId (or all its ancestors) is in a closed category.
   *
   * @param spaceId - The root space ID.
   * @param parentId - The parent space ID to start the check from.
   * @param previousId - The last ID checked, only used to ignore root collapse state.
   * @param visited - Set used to prevent recursion errors.
   * @returns True if parentId or all ancestors is in a closed category.
   */
  const getInClosedCategories = useCallback(
    (
      spaceId: string,
      parentId: string,
      previousId?: string,
      visited: Set<string> = new Set()
    ): boolean => {
      // Ignore root space being collapsed if in a subspace,
      // this is due to many spaces dumping all rooms in the top-level space.
      if (parentId === spaceId && previousId) {
        if (spaceRooms.has(previousId) || getRoom(previousId)?.isSpaceRoom()) {
          return false;
        }
      }

      const categoryId = makeNavCategoryId(spaceId, parentId);

      // Prevent infinite recursion
      if (visited.has(categoryId)) return false;
      visited.add(categoryId);

      if (closedCategoriesCache.current.has(categoryId)) {
        return closedCategoriesCache.current.get(categoryId);
      }

      if (closedCategories.has(categoryId)) {
        closedCategoriesCache.current.set(categoryId, true);
        return true;
      }

      const parentParentIds = roomToParents.get(parentId);
      if (!parentParentIds || parentParentIds.size === 0) {
        closedCategoriesCache.current.set(categoryId, false);
        return false;
      }

      // As a subspace can be in multiple spaces,
      // only return true if all parent spaces are closed.
      const allClosed = !Array.from(parentParentIds).some(
        (id) => !getInClosedCategories(spaceId, id, parentId, visited)
      );
      visited.delete(categoryId);
      closedCategoriesCache.current.set(categoryId, allClosed);
      return allClosed;
    },
    [closedCategories, getRoom, roomToParents, spaceRooms]
  );

  /**
   * Recursively checks if the given room or any of its descendants should be visible.
   *
   * @param roomId - The room ID to check.
   * @param visited - Set used to prevent recursion errors.
   * @returns True if the room or any descendant should be visible.
   */
  const getContainsShowRoom = useCallback(
    (roomId: string, visited: Set<string> = new Set()): boolean => {
      if (roomToUnread.has(roomId) || roomId === selectedRoomId) {
        return true;
      }

      // Prevent infinite recursion
      if (visited.has(roomId)) return false;
      visited.add(roomId);

      const childIds = roomToChildren.get(roomId);
      if (!childIds || childIds.size === 0) {
        return false;
      }

      return Array.from(childIds).some((id) => getContainsShowRoom(id, visited));
    },
    [roomToUnread, selectedRoomId, roomToChildren]
  );

  /**
   * Determines the depth limit for the joined space hierarchy and the SpaceNavItems to start appearing
   */
  const [subspaceHierarchyLimit] = useSetting(settingsAtom, 'subspaceHierarchyLimit');
  /**
   * Creates an SVG used for connecting spaces to their subrooms.
   * @param virtualizedItems - The virtualized item list that will be used to render elements in the nav
   * @returns React SVG Element that can be overlayed on top of the nav category for rooms.
   */
  const getConnectorSVG = (
    hierarchy: HierarchyItem[],
    virtualizedItems: VirtualItem[]
  ): ReactElement => {
    const DEPTH_START = 2;
    const PADDING_LEFT_DEPTH_OFFSET = 15.75;
    const PADDING_LEFT_DEPTH_OFFSET_START = -15.75;
    const RADIUS = 5;

    let connectorStack: { aX: number; aY: number }[] = [];
    // Holder for the paths
    const pathHolder: ReactElement[] = [];
    virtualizedItems.forEach((vItem) => {
      const hierarchyItem = hierarchy[vItem.index];
      if (!hierarchyItem) return;
      const { roomId, depth: itemDepth } = hierarchyItem;
      const depth = itemDepth ?? 0;
      const room = getRoom(roomId);
      // We will render spaces at a level above their normal depth, since we want their children to be "under" them
      const renderDepth = room?.isSpaceRoom() ? depth : depth + 1;
      // for the root items, we are not doing anything with it.
      if (renderDepth < DEPTH_START) {
        return;
      }
      // for nearly root level text/call rooms, we will not be drawing any arcs.
      if (renderDepth === DEPTH_START - 1 && !room?.isSpaceRoom() && connectorStack.length === 0) {
        return;
      }

      // for the sub-root items, we will not draw any arcs from root to it.
      // however, we should capture the aX and aY to draw starter arcs for next depths.
      if (renderDepth === DEPTH_START) {
        connectorStack = [
          {
            aX: PADDING_LEFT_DEPTH_OFFSET * DEPTH_START + PADDING_LEFT_DEPTH_OFFSET_START,
            aY: vItem.end,
          },
        ];
        return;
      }
      // adjust the stack to be at the correct depth, which is the "parent" of the current item.
      while (connectorStack.length + DEPTH_START > renderDepth && connectorStack.length !== 0) {
        connectorStack.pop();
      }

      // Fixes crash in case the top level virtual item is unrendered.
      if (connectorStack.length === 0) {
        connectorStack = [{ aX: Math.round(renderDepth * PADDING_LEFT_DEPTH_OFFSET), aY: 0 }];
      }

      const lastConnector = connectorStack[connectorStack.length - 1];
      if (!lastConnector) return;

      // aX: numeric x where the vertical connector starts
      // aY: end of parent (already numeric)
      const { aX, aY } = lastConnector;

      // bX: point where the vertical connector ends
      const bX = Math.round(
        (renderDepth - 0.5) * PADDING_LEFT_DEPTH_OFFSET + PADDING_LEFT_DEPTH_OFFSET_START
      );
      // bY: center of current item
      const bY = vItem.end - vItem.size / 2;

      const pathString =
        `M ${aX} ${aY} ` +
        `L ${aX} ${bY - RADIUS} ` +
        `A ${RADIUS} ${RADIUS} 0 0 0 ${aX + RADIUS} ${bY} ` +
        `L ${bX} ${bY}`;

      pathHolder.push(
        <path
          d={pathString}
          fill="none"
          stroke={color.Surface.ContainerLine}
          strokeWidth="2"
          display="block"
        />
      );

      // add this item to the connector stack, in case the next item's depth is higher.
      connectorStack.push({
        aX: Math.round(renderDepth * PADDING_LEFT_DEPTH_OFFSET) + PADDING_LEFT_DEPTH_OFFSET_START,
        aY: vItem.end,
      });
    });
    return (
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {pathHolder}
      </svg>
    );
  };

  const hierarchy = useSpaceJoinedHierarchy(
    space.roomId,
    getRoom,
    useCallback(
      (parentId, roomId, depth) => {
        if (parentId === space.roomId) {
          return false;
        }
        if (depth >= subspaceHierarchyLimit) {
          // we will exclude items above this depth
          return true;
        }
        if (!getInClosedCategories(space.roomId, parentId, roomId)) {
          return false;
        }
        const unread = roomToUnread.get(roomId);
        const containsShowRoom = getContainsShowRoom(roomId);
        const hasUnread = !!unread && (unread.total > 0 || unread.highlight > 0);
        const showRoomAnyway =
          hasUnread || roomId === selectedRoomId || callEmbed?.roomId === roomId;
        return containsShowRoom || !showRoomAnyway;
      },
      [
        getContainsShowRoom,
        getInClosedCategories,
        space.roomId,
        callEmbed,
        subspaceHierarchyLimit,
        roomToUnread,
        selectedRoomId,
      ]
    ),
    useCallback(
      (sId) => getInClosedCategories(space.roomId, sId),
      [getInClosedCategories, space.roomId]
    )
  );

  const getItemKey = useCallback(
    (index: number) => {
      const item = hierarchy[index];
      if (!item) return index;
      return getSpaceHierarchyItemKey(space.roomId, item);
    },
    [hierarchy, space.roomId]
  );

  const virtualizer = useVirtualizer({
    count: hierarchy.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 32,
    overscan: 10,
    getItemKey,
  });

  const virtualizedItems = virtualizer.getVirtualItems();

  const handleCategoryClick = useCategoryHandler(setClosedCategories, (categoryId) =>
    closedCategories.has(categoryId)
  );

  const getToLink = (roomId: string) => {
    const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, roomId);
    if (mx.getRoom(roomId)?.getType() === CustomRoomType.Forum) {
      return getSpaceForumPath(spaceIdOrAlias, roomIdOrAlias);
    }
    return getSpaceRoomPath(spaceIdOrAlias, roomIdOrAlias);
  };

  return (
    <PageNavShell
      header={<SpaceHeader hideText={hideText} mx={mx} />}
      curWidth={curWidth}
      setCurWidth={setCurWidth}
      roomSidebarWidth={roomSidebarWidth}
      setRoomSidebarWidth={setRoomSidebarWidth}
      setIsResizingSidebar={setIsResizingSidebar}
      isMobile={isMobile}
      oldSidebar={oldSidebar}
    >
      <PageNavContent scrollRef={scrollRef}>
        <Box direction="Column" gap="300">
          {tombstoneEvent && (
            <SpaceTombstone
              roomId={space.roomId}
              replacementRoomId={tombstoneEvent.getContent().replacement_room}
            />
          )}
          <NavCategory>
            <NavItem variant="Background" radii="400" aria-selected={lobbySelected}>
              <NavLink to={getSpaceLobbyPath(getCanonicalAliasOrRoomId(mx, space.roomId))}>
                <NavItemContent>
                  <Box as="span" grow="Yes" alignItems="Center" justifyContent="Start" gap="200">
                    <Avatar
                      size={hideText ? undefined : '200'}
                      shape={AvatarShape.Squircle}
                      style={hideText ? { width: '100%', padding: '0' } : undefined}
                    >
                      {menuIcon(Flag, { weight: lobbySelected ? 'fill' : 'regular' })}
                    </Avatar>
                    {!hideText && (
                      <Box as="span" grow="Yes">
                        <Text as="span" size="Inherit" truncate>
                          Lobby
                        </Text>
                      </Box>
                    )}
                  </Box>
                </NavItemContent>
              </NavLink>
            </NavItem>
            <NavItem variant="Background" radii="400" aria-selected={searchSelected}>
              <NavLink to={getSpaceSearchPath(getCanonicalAliasOrRoomId(mx, space.roomId))}>
                <NavItemContent>
                  <Box as="span" grow="Yes" alignItems="Center" justifyContent="Start" gap="200">
                    <Avatar
                      size={hideText ? undefined : '200'}
                      shape={AvatarShape.Squircle}
                      style={hideText ? { width: '100%' } : undefined}
                    >
                      {menuIcon(MagnifyingGlass, {
                        weight: searchSelected ? 'fill' : 'regular',
                      })}
                    </Avatar>
                    <Box as="span" grow="Yes">
                      {!hideText && (
                        <Text as="span" size="Inherit" truncate>
                          Message Search
                        </Text>
                      )}
                    </Box>
                  </Box>
                </NavItemContent>
              </NavLink>
            </NavItem>
          </NavCategory>
          {hierarchy.length > 0 && (
            <Line variant="Background" size="300" style={{ marginInline: config.space.S200 }} />
          )}
          <NavCategory
            style={{
              height: virtualizer.getTotalSize(),
              position: 'relative',
              overflow: 'visible',
            }}
          >
            {virtualizedItems.map((vItem) => {
              const hierarchyItem = hierarchy[vItem.index];
              if (!hierarchyItem) return null;
              const { roomId, depth: itemDepth } = hierarchyItem;
              const depth = itemDepth ?? 0;
              const room = mx.getRoom(roomId);
              const renderDepth = room?.isSpaceRoom() ? depth - 2 : depth - 1;
              if (!room) return null;
              if (depth === subspaceHierarchyLimit && room.isSpaceRoom()) {
                return (
                  <VirtualTile virtualItem={vItem} key={vItem.key} ref={virtualizer.measureElement}>
                    <div
                      style={
                        hideText
                          ? {}
                          : {
                              paddingLeft: `calc(${renderDepth} * ${config.space.S400})`,
                            }
                      }
                    >
                      <SpaceNavItem
                        room={room}
                        selected={selectedRoomId === roomId}
                        linkPath={getSpaceLobbyPath(getCanonicalAliasOrRoomId(mx, roomId))}
                        hideText={hideText}
                      />
                    </div>
                  </VirtualTile>
                );
              }

              const paddingTop = config.space.S400;
              const paddingLeft = `calc(${renderDepth} * ${config.space.S400})`;

              if (room.isSpaceRoom()) {
                const categoryId = makeNavCategoryId(space.roomId, roomId);
                const closedViaCategory = getInClosedCategories(space.roomId, roomId);

                return (
                  <VirtualTile virtualItem={vItem} key={vItem.key} ref={virtualizer.measureElement}>
                    <div style={hideText ? { paddingTop: '0' } : { paddingTop, paddingLeft }}>
                      <NavCategoryHeader style={hideText ? { justifyContent: 'Center' } : {}}>
                        <RoomNavCategoryButton
                          data-category-id={categoryId}
                          onClick={handleCategoryClick}
                          closed={closedCategories.has(categoryId) || closedViaCategory}
                        >
                          {!hideText && room?.name}
                        </RoomNavCategoryButton>
                      </NavCategoryHeader>
                    </div>
                  </VirtualTile>
                );
              }

              return (
                <VirtualTile virtualItem={vItem} key={vItem.key} ref={virtualizer.measureElement}>
                  <div
                    style={
                      hideText
                        ? {
                            padding: '0',
                            width: '100%',
                            aspectRatio: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }
                        : { paddingLeft }
                    }
                  >
                    <RoomNavItem
                      room={room}
                      selected={selectedRoomId === roomId}
                      showAvatar={mDirects.has(roomId) || showIcons()}
                      direct={mDirects.has(roomId)}
                      linkPath={getToLink(roomId)}
                      hideText={hideText}
                      notificationMode={getRoomNotificationMode(
                        notificationPreferences,
                        room.roomId
                      )}
                      joinCallOnSingleClick={joinCallOnSingleClick}
                      isStrict={showRoomIcon === ShowRoomIcon.Strict}
                    />
                  </div>
                </VirtualTile>
              );
            })}
            {getConnectorSVG(hierarchy, virtualizedItems)}
          </NavCategory>
          {!isMobile && <div style={{ height: toRem(40) }} />}
        </Box>
      </PageNavContent>
    </PageNavShell>
  );
}
